import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useState, useRef, useEffect } from 'react';
import { openCalendly } from '../../lib/calendly';
import BsiqActionButton from './BsiqActionButton';
import { openApp } from '../../lib/siteUrls';

const LandingNav = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [featuresOpen, setFeaturesOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('');
  const featuresRef = useRef(null);
  const featuresButtonRef = useRef(null);
  const menuButtonRef = useRef(null);
  const closeTimer = useRef(null);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (featuresRef.current && !featuresRef.current.contains(e.target)) {
        setFeaturesOpen(false);
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  useEffect(() => {
    const onEscape = (event) => {
      if (event.key !== 'Escape') return;

      if (featuresOpen) {
        setFeaturesOpen(false);
        featuresButtonRef.current?.focus();
      } else if (menuOpen) {
        setMenuOpen(false);
        menuButtonRef.current?.focus();
      }
    };

    document.addEventListener('keydown', onEscape);
    return () => document.removeEventListener('keydown', onEscape);
  }, [featuresOpen, menuOpen]);

  useEffect(() => {
    if (location.pathname !== '/') return undefined;

    const sections = ['oi', 'atlas', 'pricing']
      .map((id) => document.getElementById(id))
      .filter(Boolean);
    if (!sections.length) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (visible) setActiveSection(visible.target.id);
      },
      { rootMargin: '-20% 0px -65% 0px', threshold: [0, 0.1, 0.35] },
    );

    sections.forEach((section) => observer.observe(section));
    return () => observer.disconnect();
  }, [location.pathname]);

  const scrollTo = (id) => {
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 400);
    } else {
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const openFeatures = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setFeaturesOpen(true);
  };
  const scheduleCloseFeatures = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setFeaturesOpen(false), 220);
  };

  const featureItems = [
    { id: 'oi',       icon: 'target',                bg: '#EAF5FC', fg: '#1E5F8F', title: 'Eligibility Scoring',   desc: 'Evidence-first bid qualification before you write a word' },
    { id: 'oi',       icon: 'bolt',                  bg: '#FEF3C7', fg: '#8A6700', title: 'Amendment Intelligence', desc: 'Track material changes, deadlines, and required action' },
    { id: 'atlas',    icon: 'smart_toy',             bg: '#EAF5FC', fg: '#1E5F8F', title: 'Atlas',                 desc: 'GovCon intelligence attached to every pursuit' },
    { id: 'atlas',    icon: 'monitoring',            bg: '#EEF2F7', fg: '#0D1F3C', title: 'Pricing War Room',       desc: 'BLS, GSA CALC, and award evidence in one reviewable model' },
    { id: 'ei',       icon: 'edit_note',             bg: '#FEF3C7', fg: '#8A6700', title: 'Proposal Workspace',     desc: 'Source-grounded Section L/M-aligned drafts and compliance' },
  ];

  const navLinkClass = (isActive = false) =>
    `bsiq-nav-link${isActive ? ' bsiq-nav-link--active' : ''}`;

  return (
    <nav className="fixed top-0 w-full z-50 border-b border-[#DCE4ED] bsiq-marketing-nav" data-testid="landing-nav" style={{ fontFamily: "'Public Sans', sans-serif" }}>
      <div className="flex justify-between items-center px-6 py-3.5 max-w-7xl mx-auto gap-4">
        <div className="flex items-center gap-4 flex-shrink-0">
          {/* Logo — image only, no wordmark */}
          <Link
            to="/"
            className="flex items-center bsiq-focus-ring rounded-sm"
            aria-label="BidsignalIQ home"
            data-testid="landing-logo"
          >
            <img
              src="/bidsignaliq-logo.png?v=20261021"
              alt=""
              style={{ height: '52px', width: 'auto', display: 'block' }}
            />
          </Link>

          {/* Mobile menu toggle */}
          <button
            ref={menuButtonRef}
            onClick={() => { setMenuOpen(!menuOpen); setFeaturesOpen(false); }}
            className="lg:hidden text-sm font-bold px-4 py-2 rounded hover:bg-slate-100 transition-colors inline-flex items-center gap-1.5 bsiq-focus-ring"
            style={{ color: '#0D1F3C' }}
            data-testid="nav-menu-toggle"
            aria-expanded={menuOpen}
            aria-controls="landing-mobile-menu"
            aria-label={menuOpen ? 'Close navigation menu' : 'Open navigation menu'}
          >
            {menuOpen ? (
              <>Close <span className="text-xs">✕</span></>
            ) : (
              <>Menu</>
            )}
          </button>
        </div>

        {/* Persistent enterprise navigation */}
        <div
          className="hidden lg:flex items-center gap-1 flex-1 justify-center"
          data-testid="nav-pills-container"
        >
          {/* Features with dropdown */}
          <div
            className="relative"
            ref={featuresRef}
            onMouseEnter={openFeatures}
            onMouseLeave={scheduleCloseFeatures}
          >
            <button
              ref={featuresButtonRef}
              className={navLinkClass(featuresOpen)}
              onClick={() => setFeaturesOpen(!featuresOpen)}
              data-testid="nav-pill-features"
              aria-expanded={featuresOpen}
              aria-controls="landing-product-menu"
            >
              <span>Product</span>
              <span
                className={`material-symbols-outlined bsiq-nav-link__chevron${featuresOpen ? ' bsiq-nav-link__chevron--open' : ''}`}
                aria-hidden="true"
              >
                expand_more
              </span>
            </button>

            {featuresOpen && (
              <div
                id="landing-product-menu"
                className="absolute left-0 top-full mt-2 w-[380px] bsiq-fade"
                style={{
                  backgroundColor: '#FFFFFF',
                  border: '1px solid #E5E7EB',
                  borderRadius: '12px',
                  boxShadow: '0 12px 32px rgba(13,17,23,0.12)',
                  padding: '10px',
                }}
                onMouseEnter={openFeatures}
                onMouseLeave={scheduleCloseFeatures}
                data-testid="nav-features-dropdown"
                aria-label="Product navigation"
              >
                {featureItems.map((it, i) => (
                  <button
                    key={i}
                    onClick={() => { scrollTo(it.id); setFeaturesOpen(false); setMenuOpen(false); }}
                    className="w-full flex items-start gap-3 p-3 rounded-lg transition-colors hover:bg-[#F5F1EC] text-left"
                  >
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: it.bg, color: it.fg }}>
                      <span className="material-symbols-outlined text-base">{it.icon}</span>
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold" style={{ color: '#0D1F3C' }}>{it.title}</p>
                      <p className="text-xs mt-0.5" style={{ color: '#6B7280' }}>{it.desc}</p>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          <button className={navLinkClass(activeSection === 'oi')} aria-current={activeSection === 'oi' ? 'location' : undefined} onClick={() => { scrollTo('oi'); setMenuOpen(false); }} data-testid="nav-pill-how">How It Works</button>
          <button className={navLinkClass(activeSection === 'atlas')} aria-current={activeSection === 'atlas' ? 'location' : undefined} onClick={() => { scrollTo('atlas'); setMenuOpen(false); }} data-testid="nav-pill-atlas">Atlas</button>
          <button className={navLinkClass(location.pathname === '/trust-center')} aria-current={location.pathname === '/trust-center' ? 'page' : undefined} onClick={() => navigate('/trust-center')} data-testid="nav-pill-security">Security</button>
          <button className={navLinkClass(activeSection === 'pricing')} aria-current={activeSection === 'pricing' ? 'location' : undefined} onClick={() => { scrollTo('pricing'); setMenuOpen(false); }} data-testid="nav-pill-pricing">Pricing</button>
        </div>

        {/* Right side */}
        <div className="flex items-center gap-3 flex-shrink-0">
          <BsiqActionButton
            onClick={openCalendly}
            variant="secondary"
            className="hidden sm:inline-flex bsiq-action--nav"
            data-testid="nav-request-demo-btn"
          >
            Request demo
          </BsiqActionButton>
          <BsiqActionButton
            onClick={() => openApp('/login')}
            className="bsiq-action--nav"
            data-testid="nav-signin-btn"
          >
            Sign In
          </BsiqActionButton>
        </div>
      </div>

      {menuOpen && (
        <div
          id="landing-mobile-menu"
          className="lg:hidden border-t border-[#E5E7EB] bg-white px-6 py-5 bsiq-fade"
          data-testid="landing-mobile-menu"
        >
          <div className="grid gap-1 max-w-7xl mx-auto">
            {[
              ['Product', 'oi'],
              ['How It Works', 'oi'],
              ['Atlas', 'atlas'],
              ['Pricing', 'pricing'],
              ['Atlas Desktop', 'download'],
            ].map(([label, id]) => (
              <button
                key={label}
                onClick={() => { scrollTo(id); setMenuOpen(false); }}
                className="w-full min-h-11 rounded-lg px-4 py-3 text-left text-sm font-semibold hover:bg-[#F5F1EC] bsiq-focus-ring"
                style={{ color: '#0D1F3C' }}
              >
                {label}
              </button>
            ))}
            <button
              onClick={() => { navigate('/trust-center'); setMenuOpen(false); }}
              className="w-full min-h-11 rounded-lg px-4 py-3 text-left text-sm font-semibold hover:bg-[#F5F1EC] bsiq-focus-ring"
              style={{ color: '#0D1F3C' }}
            >
              Security &amp; Trust
            </button>
            <button
              onClick={() => { setMenuOpen(false); openCalendly(); }}
              className="mt-3 min-h-11 rounded-lg px-4 py-3 text-sm font-semibold bsiq-focus-ring"
              style={{ backgroundColor: '#0D1F3C', color: '#FFFFFF' }}
            >
              Request a demo
            </button>
          </div>
        </div>
      )}
    </nav>
  );
};

export default LandingNav;
